import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Dropdown, Label } from '@heroui/react';
import { DataGrid, NumberValue, Widget } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { formatInLanguage } from '@/utils/dateLocale';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { InvoiceTotal } from '../invoiceTotal';
import {
    ACTIVE_DRAFT_ROW_ACTIONS,
    EMPTY_FIELD_PLACEHOLDER,
    compareNullableNumber,
    compareNullableString,
    formatInvoiceNumber,
    type ActiveDraft,
    type ActiveDraftRowAction,
} from './activeDraftsMeta';

type ActiveDraftsViewProps = {
    items: ActiveDraft[];
    onDelete: (draftId: string) => void;
    onEdit: (draftId: string) => void;
};

/**
 * Editable drafts table. Mirrors `PendingInvoicesView`'s structure
 * with three intentional differences:
 *   - the header icon tile uses the `warning` semantic colour —
 *     drafts are "needs attention / incomplete" — to distinguish them
 *     from the in-progress accent of pending;
 *   - every domain field is nullable; empty values render as a muted
 *     em-dash so the row's vertical rhythm doesn't collapse when a
 *     cell is blank, and the sortable columns use null-safe comparators
 *     that push missing values to the end;
 *   - the pinned actions column hosts the shared ellipsis dropdown
 *     (Edit + a danger-styled Delete), matching the Categories /
 *     Suppliers tables.
 */
export const ActiveDraftsView: React.FC<ActiveDraftsViewProps> = ({
    items,
    onDelete,
    onEdit,
}) => {
    const { t, i18n } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('purchases.widgets.activeDrafts.title'),
            subtitle: (count: number) =>
                t('purchases.widgets.activeDrafts.subtitle', { count }),
            tableAria: t('purchases.widgets.activeDrafts.tableAria'),
            actionsAria: t('purchases.widgets.activeDrafts.actionsAria'),
            columns: {
                invoice: t('purchases.widgets.activeDrafts.columns.invoice'),
                supplier: t('purchases.widgets.activeDrafts.columns.supplier'),
                date: t('purchases.widgets.activeDrafts.columns.date'),
                productCount: t(
                    'purchases.widgets.activeDrafts.columns.productCount',
                ),
                totalAmount: t(
                    'purchases.widgets.activeDrafts.columns.totalAmount',
                ),
            },
            empty: {
                title: t('purchases.widgets.activeDrafts.empty.title'),
            },
        }),
        [t],
    );

    const onRowAction = React.useCallback(
        (draftId: string, action: ActiveDraftRowAction) => {
            if (action === 'edit') {
                onEdit(draftId);
                return;
            }
            onDelete(draftId);
        },
        [onDelete, onEdit],
    );

    // Clicking anywhere on a row resumes the draft (same as the Edit action).
    const onRowPress = React.useCallback(
        (key: string | number) => onEdit(String(key)),
        [onEdit],
    );

    const columns = React.useMemo<DataGridColumn<ActiveDraft>[]>(
        () => [
            {
                id: 'invoice',
                header: copy.columns.invoice,
                isRowHeader: true,
                width: 150,
                allowsSorting: true,
                sortFn: (a, b) =>
                    compareNullableString(a.invoiceNumber, b.invoiceNumber),
                cell: (item) =>
                    item.invoiceNumber !== null ? (
                        <span
                            className="font-mono text-xs font-bold text-foreground"
                            {...getNodeTestIdProp(
                                `active-drafts-row-number-${item.id}`,
                            )}
                        >
                            {formatInvoiceNumber(item.invoiceNumber)}
                        </span>
                    ) : (
                        <span
                            className="text-xs text-muted"
                            {...getNodeTestIdProp(
                                `active-drafts-row-number-empty-${item.id}`,
                            )}
                        >
                            {EMPTY_FIELD_PLACEHOLDER}
                        </span>
                    ),
            },
            {
                id: 'supplier',
                header: copy.columns.supplier,
                allowsSorting: true,
                sortFn: (a, b) => compareNullableString(a.supplier, b.supplier),
                cell: (item) =>
                    item.supplier !== null ? (
                        <span
                            className="truncate text-sm text-foreground"
                            {...getNodeTestIdProp(
                                `active-drafts-row-supplier-${item.id}`,
                            )}
                        >
                            {item.supplier}
                        </span>
                    ) : (
                        <span
                            className="text-sm text-muted"
                            {...getNodeTestIdProp(
                                `active-drafts-row-supplier-empty-${item.id}`,
                            )}
                        >
                            {EMPTY_FIELD_PLACEHOLDER}
                        </span>
                    ),
            },
            {
                id: 'date',
                header: copy.columns.date,
                width: 150,
                allowsSorting: true,
                sortFn: (a, b) => compareNullableString(a.date, b.date),
                cell: (item) =>
                    item.date !== null ? (
                        <span
                            className="text-xs text-muted"
                            {...getNodeTestIdProp(
                                `active-drafts-row-date-${item.id}`,
                            )}
                        >
                            {formatInLanguage(
                                item.date,
                                'D MMM YYYY · HH:mm',
                                i18n.language,
                            )}
                        </span>
                    ) : (
                        <span
                            className="text-xs text-muted"
                            {...getNodeTestIdProp(
                                `active-drafts-row-date-empty-${item.id}`,
                            )}
                        >
                            {EMPTY_FIELD_PLACEHOLDER}
                        </span>
                    ),
            },
            {
                id: 'productCount',
                header: copy.columns.productCount,
                width: 110,
                align: 'end',
                allowsSorting: true,
                sortFn: (a, b) =>
                    compareNullableNumber(a.productCount, b.productCount),
                cell: (item) =>
                    item.productCount !== null ? (
                        <NumberValue
                            className="text-sm tabular-nums text-foreground"
                            maximumFractionDigits={0}
                            minimumFractionDigits={0}
                            style="decimal"
                            value={item.productCount}
                            {...getNodeTestIdProp(
                                `active-drafts-row-product-count-${item.id}`,
                            )}
                        />
                    ) : (
                        <span
                            className="text-sm text-muted"
                            {...getNodeTestIdProp(
                                `active-drafts-row-product-count-empty-${item.id}`,
                            )}
                        >
                            {EMPTY_FIELD_PLACEHOLDER}
                        </span>
                    ),
            },
            {
                id: 'totalAmount',
                header: copy.columns.totalAmount,
                width: 140,
                align: 'end',
                allowsSorting: true,
                sortFn: (a, b) =>
                    compareNullableNumber(a.totalAmount, b.totalAmount),
                cell: (item) =>
                    item.totalAmountTetri !== null ? (
                        <InvoiceTotal
                            expensesTotalTetri={item.expensesTotalTetri ?? 0}
                            linesTotalTetri={
                                item.linesTotalTetri ?? item.totalAmountTetri
                            }
                            rowId={item.id}
                            slot="active-drafts-row"
                            totalAmountTetri={item.totalAmountTetri}
                        />
                    ) : (
                        <span
                            className="text-sm text-muted"
                            {...getNodeTestIdProp(
                                `active-drafts-row-total-empty-${item.id}`,
                            )}
                        >
                            {EMPTY_FIELD_PLACEHOLDER}
                        </span>
                    ),
            },
            {
                id: 'actions',
                header: '',
                pinned: 'end',
                width: 40,
                align: 'center',
                cellClassName: 'bg-surface',
                headerClassName: 'bg-surface-secondary',
                cell: (item) => (
                    <div
                        className="flex justify-center"
                        {...getNodeTestIdProp(
                            `active-drafts-row-actions-${item.id}`,
                        )}
                    >
                        <Dropdown>
                            <Button
                                aria-label={copy.actionsAria}
                                isIconOnly
                                size="sm"
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    `active-drafts-row-actions-trigger-${item.id}`,
                                )}
                            >
                                <Icon icon="gravity-ui:ellipsis-vertical" />
                            </Button>
                            <Dropdown.Popover className="min-w-[160px]">
                                <Dropdown.Menu
                                    onAction={(key) =>
                                        onRowAction(
                                            item.id,
                                            key as ActiveDraftRowAction,
                                        )
                                    }
                                >
                                    {ACTIVE_DRAFT_ROW_ACTIONS.map((action) => (
                                        <Dropdown.Item
                                            id={action.id}
                                            key={action.id}
                                            textValue={t(action.labelKey)}
                                            variant={action.variant}
                                            {...getNodeTestIdProp(
                                                `active-drafts-row-action-${action.id}-${item.id}`,
                                            )}
                                        >
                                            <Icon icon={action.icon} />
                                            <Label>{t(action.labelKey)}</Label>
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown.Popover>
                        </Dropdown>
                    </div>
                ),
            },
        ],
        [copy, i18n.language, onRowAction, t],
    );

    return (
        <section
            aria-labelledby="active-drafts-widget-title"
            {...getNodeTestIdProp('active-drafts-section')}
        >
            <Widget
                className="flex flex-col gap-0 overflow-hidden p-0"
                {...getNodeTestIdProp('active-drafts-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('active-drafts-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('active-drafts-widget-title')}
                    >
                        <div
                            aria-hidden
                            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-foreground/10 text-foreground"
                            {...getNodeTestIdProp('active-drafts-icon-wrap')}
                        >
                            <Icon
                                className="size-4"
                                icon="solar:pen-new-square-bold-duotone"
                                {...getNodeTestIdProp('active-drafts-icon')}
                            />
                        </div>
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp('active-drafts-widget-subtitle')}
                    >
                        {copy.subtitle(items.length)}
                    </Widget.Description>
                </Widget.Header>
                <DataGrid<ActiveDraft>
                    aria-label={copy.tableAria}
                    columns={columns}
                    data={items}
                    defaultSortDescriptor={{
                        column: 'invoice',
                        direction: 'ascending',
                    }}
                    getRowId={(item) => item.id}
                    onRowAction={onRowPress}
                    renderEmptyState={() => (
                        <div
                            className="flex flex-col items-center gap-3 py-12 text-muted"
                            {...getNodeTestIdProp('active-drafts-empty')}
                        >
                            <Icon
                                aria-hidden
                                className="size-8 text-default-500"
                                icon="solar:pen-new-square-bold-duotone"
                                {...getNodeTestIdProp(
                                    'active-drafts-empty-icon',
                                )}
                            />
                            <span
                                className="text-sm"
                                {...getNodeTestIdProp(
                                    'active-drafts-empty-title',
                                )}
                            >
                                {copy.empty.title}
                            </span>
                        </div>
                    )}
                    scrollContainerClassName="max-h-[520px] overflow-y-auto"
                    variant="primary"
                    {...getNodeTestIdProp('active-drafts-grid')}
                />
            </Widget>
        </section>
    );
};
